<template>
  <div>
    <div
      v-if="Array.isArray(modelValue) && modelValue.length"
      class="d-flex align-items-center flex-wrap mb-1"
    >
      <b-form-checkbox
        v-for="badge in modelValue"
        v-model="selectedItems"
        @change="onBadgesChange"
        :key="badge"
        :value="badge"
        class="mr-3 mb-1"
      >
        <small>{{ badge }}</small>
      </b-form-checkbox>
    </div>

    <div class="d-md-flex align-items-center">
      <div class="w-100" data-toggle="buttons">
        <button
          v-loading="loading"
          @click="showModal = !showModal"
          :class="{
            active: Array.isArray(modelValue) && modelValue.length
          }"
          :disabled="loading || !options.length"
          class="btn badge badge-pill text-capitalize modal-toggle"
        >
          Select Report Date ({{ options.length }})
        </button>
      </div>

      <span class="w-25 px-2 text-center">OR</span>

      <DatePicker
        v-model="dateRange"
        @input="emitDates"
        class="w-100"
        format="MM-DD-YYYY"
        input-class="form-control form-control-sm rounded-sm"
        prefix-class="xmx"
        range-separator=" to "
        range
      />

      <span class="w-25 px-2 text-center">OR</span>

      <b-form-group v-slot="{ ariaDescribedby }" class="w-100">
        <b-form-radio-group
          v-model="modelValue"
          :options="dateOptions"
          :aria-describedby="ariaDescribedby"
          stacked
        ></b-form-radio-group>
      </b-form-group>
    </div>

    <b-modal
      v-model="showModal"
      title="Reporting Period Dates"
      body-class="py-0 px-4"
      footer-class="p-4 d-flex align-items-center justify-content-between"
      class="drv-modal"
      no-close-on-backdrop
      no-close-on-esc
    >
      <VueOptionsList
        v-model="selectedItems"
        v-loading="loading"
        :options="sortedOptions"
      />

      <template #modal-footer>
        <b-button
          v-if="selectedItems.length"
          @click.prevent.stop="() => handleSave({ resetItems: true })"
          :disabled="loading"
          variant="primary"
          outlined
          >Clear</b-button
        >
        <b-button
          @click.prevent.stop="handleSave"
          :disabled="loading"
          variant="primary"
          class="ml-auto"
          >Save</b-button
        >
      </template>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment'
import { debounce } from 'lodash-es'
import DatePicker from 'vue2-datepicker'

import { DATE_FORMAT } from '@constants'
import { exportMixin, userMixin } from '@mixins'

export default {
  props: {
    value: [String, Array, Object],
    loading: Boolean,
    options: {
      type: Array,
      default: () => []
    }
  },
  components: { DatePicker },
  data() {
    return {
      dateRange: [],
      showModal: false,
      excludeOptions: [],
      selectedItems: [],
      isListPopulated: false
    }
  },
  mixins: [exportMixin, userMixin],
  computed: {
    modelValue: {
      get() {
        if (!this.value) return null

        if ('string' === typeof this.value) {
          return this.value
        } else {
          const startMonth = moment().startOf('month')
          const endMonth = moment().endOf('month')

          if (
            this.value.from ===
              startMonth.subtract(1, 'month').format('MM-DD-YYYY') &&
            this.value.to === endMonth.subtract(1, 'month').format('MM-DD-YYYY')
          )
            return 'prev-month'
          else return this.value
        }
      },
      set(newValue) {
        this.dateRange = null
        this.selectedItems = []

        if (!newValue) {
          this.emitDates(null)
        } else if ('string' == typeof newValue) {
          const startMonth = moment().startOf('month')
          const endMonth = moment().endOf('month')

          if ('prev-month' === newValue) {
            startMonth.subtract(1, 'month')
            endMonth.subtract(1, 'month')
          }

          this.emitDates([startMonth.toDate(), endMonth.toDate()])
        } else if (Array.isArray(newValue)) {
          this.selectedItems = newValue
          this.isListPopulated = newValue.length ? true : false
        } else {
          this.dateRange = [
            moment(newValue.from, 'MM-DD-YYYY').toDate(),
            moment(newValue.to, 'MM-DD-YYYY').toDate()
          ]
        }
      }
    },
    sortedOptions() {
      return this.options
        .sort((a, b) => {
          const timestampA = moment(b, DATE_FORMAT).valueOf()
          const timestampB = moment(a, DATE_FORMAT).valueOf()

          if (timestampA === timestampB) return 0

          return timestampB < timestampA ? 1 : -1
        })
        .map((item) => moment(item).format('MM-DD-YYYY'))
    },
    dateOptions() {
      return [
        { value: null, text: 'All' },
        {
          value: 'prev-month',
          text: 'Last Month'
        }
      ].map((item) => {
        item.disabled = this.excludeOptions.includes(item.value)

        return item
      })
    }
  },
  methods: {
    emitDates(value, isSelection = false) {
      if (value && value.filter((item) => item).length)
        this.$emit(
          'input',
          isSelection
            ? value
            : {
                from: moment(value[0]).format('MM-DD-YYYY'),
                to: moment(value[1]).format('MM-DD-YYYY')
              }
        )
      else this.$emit('input', null)
    },
    async checkPeriodOptions(period) {
      const response = await this.checkParameterDatasByColumn(
        'report-date',
        'Reporting_Period_End_Date',
        this.companyRole,
        this.companyRoleValue,
        {
          period: period
        }
      )

      if (!response.data.data.Reporting_Period_End_Date.length)
        this.excludeOptions.push(period)
    },
    onBadgesChange: debounce(function (value) {
      this.emitDates(this.selectedItems, true)
    }, 500),
    handleSave({ resetItems } = {}) {
      if (undefined !== resetItems) this.selectedItems = []

      this.showModal = undefined !== resetItems ? true : false
      this.isListPopulated = this.selectedItems.length ? true : false

      this.emitDates(this.selectedItems, true)
    }
  },
  watch: {
    showModal: function (newValue) {
      if (!newValue) return

      if (this.modelValue) this.selectedItems = this.modelValue
    },
    selectedItems: debounce(function (newValue, oldValue) {
      if (!this.isListPopulated || !oldValue.length) return false

      if (JSON.stringify(newValue) != JSON.stringify(oldValue))
        this.emitDates(newValue, true)

      this.isListPopulated =
        newValue.length && JSON.stringify(newValue) == JSON.stringify(oldValue)
          ? true
          : false
    }, 500)
  },
  created() {
    // await this.checkPeriodOptions("prev-month");

    if (this.modelValue && this.modelValue.from && this.modelValue.to)
      this.dateRange = [
        moment(this.modelValue.from, 'MM-DD-YYYY').toDate(),
        moment(this.modelValue.to, 'MM-DD-YYYY').toDate()
      ]
  }
}
</script>
